import { createContext, useContext } from 'react';

import type { TruncationMode } from '@dynatrace/strato-components/typography';
import {
  FormatOptions,
  ConvertibleUnit,
  Unit,
  format,
} from '@dynatrace-sdk/units';

import { isFormatterOptions, Formatter } from '../../core/types/formatter.js';
import { TRUNCATION_MODE } from '../constants.js';

export type FormatterContextProps = {
  /**
   * Custom or default formatter for the chart
   * @defaultValue the default formatter
   */
  formatter: Formatter | FormatOptions<Unit, ConvertibleUnit>;
  /**
   * Truncation mode to use (start, middle, end)
   * Applied to all the parts that truncate text.
   * @defaultValue 'middle'
   */
  truncationMode: TruncationMode;
};

export const FormatterContext = createContext<FormatterContextProps>({
  formatter: () => '',
  truncationMode: TRUNCATION_MODE.Middle,
});

export const useFormatter = (): Formatter => {
  const { formatter } = useContext(FormatterContext);

  if (isFormatterOptions(formatter)) {
    return (value: number) => format(value, formatter);
  }

  return formatter;
};

export const useTruncationMode = (): TruncationMode => {
  const { truncationMode } = useContext(FormatterContext);

  return truncationMode;
};
